import { makeStyles } from "@material-ui/core/styles";
import { RemoveCircleOutline, RestoreFromTrash, TableChart } from "@material-ui/icons";
import { Fragment, ReactElement, useState } from 'react';
import {
    BulkActionProps,
    BulkDeleteButton,
    Button,
    ChipFieldProps,
    CreateButton,
    Datagrid,
    DateField,
    EditButton,
    EmailField,
    ExportButton,
    Identifier,
    List,
    ListProps,
    Toolbar,
    useGetMany,
    usePermissions,
    useRecordContext,
    useRefresh,
    useTranslate,
    useUnselectAll
} from 'react-admin';
import { Link } from 'react-router-dom';
import download from 'downloadjs';

import Chip from "@material-ui/core/Chip";
import { useNotify } from "ra-core";
import { RootStateOrAny, useSelector } from "react-redux";
import { Route } from "react-router";
import SideDrawer from "../../components/list/SideDrawer";
import TextField from "../../components/list/TextField";
import axiosForApi from "../../config/axios-for-api";
import { handleError } from "../../functions/common";
import { list } from "../../styles/list";
import UserCreate from "../users/UserCreate";
import UserEdit from "../users/UserEdit";
import TestSap from './TestSap';
import UserFilter from "./UserFilter";

const useStyles = makeStyles(
    {
        active: {backgroundColor: '#D9EAD3'},
        deleted: {backgroundColor: '#F4CCCC'},
        chip: {margin: 4, cursor: 'inherit'},
    });

const StatusField = ({record, ...props}: ChipFieldProps) => {
    const label = record?.deleted_at ? 'deleted' : record?.status;
    const classes = useStyles(props);
    const _className = [classes.chip];

    if (record?.deleted_at) _className.push(classes.deleted);
    else if (record?.status === 'active') _className.push(classes.active);

    return <Chip label={label} className={_className.join(" ")}/>;
};

const Has2FaField = ({record, ...props}: ChipFieldProps) => {
    const label = record?.has2fa;
    const classes = useStyles(props);
    const _className = [classes.chip];

    if (record?.has2fa === 'active') _className.push(classes.active);

    return <Chip label={label} className={_className.join(" ")}/>;
};


type TStatus = 'active' | 'inactive' | 'trashed';

type TFilters = {
    status?: TStatus;
}

const BulkActions = ({selectedIds, ...props}: BulkActionProps): ReactElement | null => {
    const {permissions} = usePermissions();

    const {data, loading} = useGetMany(
        'admin/users',
        selectedIds as Identifier[]
    );

    const router = useSelector((state: RootStateOrAny) => state.router);
    const filters = ((): TFilters => {
        try {
            return JSON.parse(decodeURIComponent(router.location.query.filter));
        } catch (e) {
        }

        return {};
    })();

    const refresh = useRefresh();
    const unselectAll = useUnselectAll('admin/users');
    const notify = useNotify();

    const status = filters.status ?? null;

    if (!data || loading) {
        return null;
    }

    const handleRestore = async () => {
        axiosForApi.post('api/admin/users/restore', {
            ids: selectedIds,
        })
            .then(() => {
                unselectAll();
                refresh();
                notify('Oooook!', 'success')
            })
            .catch(e => {
                notify(e.response.message ?? handleError(e), 'error');
                refresh();
            });
    }

    const handleRemove2fa = async () => {
        axiosForApi.post('api/admin/remove-2fa', {
            ids: selectedIds,
        })
            .then(() => {
                unselectAll();
                refresh();
                notify('2FA Removed!', 'success');
            })
            .catch(e => notify(e.response.message ?? handleError(e), 'error'));
    }

    if(!['admin', 'location-manager'].includes(permissions)) return null;

    return (
        <>
            {status !== 'trashed' &&
                <>
                    <BulkDeleteButton selectedIds={selectedIds} {...props} />
                    <Button
                        variant='text'
                        type="button"
                        color="primary"
                        label="remove2fa"
                        onClick={handleRemove2fa}
                    >
                        {<RemoveCircleOutline />}
                    </Button>
                </>
            }
            {["admin"].includes(permissions) && status === 'trashed' &&
                <Button
                    type="button"
                    color="primary"
                    label="restore"
                    onClick={handleRestore}
                    style={{backgroundColor:'#D9EAD3'}}
                >{<RestoreFromTrash />}</Button>
            }
        </>
    )
};

const ActionButtons = (props:any) => {
    return <div style={{display: 'flex', alignItems:'center', gap:'.5rem'}}>
        {!props.record.deleted_at && <EditButton {...props} />}
        <Button style={{flexShrink:0}} label="Test SAP" component={Link} to={`${props.basePath}/${props.record.id}/test-sap`} color="primary" variant="text" />
    </div>
};

const ListActions = ({basePath = ''}) => {
    let classes = list();
    const {permissions} = usePermissions();
    const notify = useNotify();
    const translate = useTranslate();
    const [exportingXlsx, setExportingXlsx] = useState(false);
    const router = useSelector((state: RootStateOrAny) => state.router);

    const getExportXlsxParams = (): Record<string, string | number | undefined> => {
        try {
            const query = router?.location?.query;
            if (!query?.filter) return {};
            const filter = JSON.parse(decodeURIComponent(query.filter));
            const params: Record<string, string | number | undefined> = { ...filter };
            if (Array.isArray(params.locations)) {
                params.locations = params.locations.join(',');
            }
            return params;
        } catch {
            return {};
        }
    };

    const handleExportXlsx = () => {
        setExportingXlsx(true);
        const params = getExportXlsxParams();
        axiosForApi.get('api/admin/users/export-xlsx', { params, responseType: 'blob' })
            .then((response) => {
                const contentType = response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                const date = new Date().toISOString().slice(0, 10);
                const filename = `users-export-${date}.xlsx`;
                download(response.data, filename, contentType);
                notify(translate('resources.admin/users.messages.export_xlsx_success'), 'success');
            })
            .catch((e) => {
                notify(e?.response?.data?.message ?? handleError(e), 'error');
            })
            .finally(() => setExportingXlsx(false));
    };

    return (
        <Toolbar className={classes.toolbar}>
            {permissions === 'admin' && <CreateButton basePath={basePath}/>}
            <ExportButton />
            <Button
                label="resources.admin/users.buttons.export_xlsx"
                onClick={handleExportXlsx}
                disabled={exportingXlsx}
                startIcon={<TableChart />}
            />
        </Toolbar>
    );
}

const LocationsField = (props:any) => {
     const record = useRecordContext(props);
    return <span>{(record.locations??[]).join(", ")}</span>;
}

const UserList = (props: ListProps): ReactElement => {
    const {permissions} = usePermissions();//=role

    return (
        <Fragment>
            <List {...props} filters={<UserFilter/>} sort={{field: 'id', order: 'DESC'}} actions={<ListActions/>} bulkActionButtons={<BulkActions />}>
                <Datagrid>
                    <TextField source="id"/>
                    <TextField source="register_method"/>
                    <Has2FaField source="has2fa"/>
                    <StatusField source="status"/>
                    <TextField source="sap_review_status"/>
                    <TextField source="role"/>
                    <TextField source="first_name"/>
                    <TextField source="last_name"/>
                    <TextField source="company"/>
                    <TextField source="kunnr_parnr" label="KUNNR/PARNR." sortable={false} />
                    <EmailField source="email"/>
                    <DateField source="email_verified_at" locales={'de'} showTime={true}/>
                    <TextField source="country"/>

                    {["admin"].includes(permissions) && <LocationsField source="locations" />}
                    <TextField source="locale"/>
                    <ActionButtons />
                </Datagrid>
            </List>
            <Route path={`${props.basePath}/create`} exact>
                {({match}) => {
                    return (
                        <SideDrawer open={!!match} basePath={props.basePath ?? ''}>
                            <UserCreate {...props} />
                        </SideDrawer>
                    )
                }}
            </Route>
            <Route path={`${props.basePath}/:id/test-sap`} exact>
                {({match}) => {
                    return (
                        <SideDrawer open={!!match} basePath={props.basePath??''} size={800}>
                            <TestSap id={match && match.params.id} {...props} />
                        </SideDrawer>
                    );
                }}
            </Route>
            <Route path={`${props.basePath}/:id`} exact>
                {({match}) => {
                    const isMatch = !!(match && match.params && !["create", "test-sap"].includes(match.params.id));
                    return (
                        <SideDrawer open={isMatch} basePath={props.basePath ?? ''} size={800}>
                            {isMatch && (
                                <UserEdit id={match && isMatch ? match.params.id : undefined}{...props}/>
                            )}
                        </SideDrawer>
                    );
                }}
            </Route>
        </Fragment>
    );
};

export default UserList;
